import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Injectable, Injector, OnDestroy, ViewContainerRef } from '@angular/core'; 
import { Router } from '@angular/router';
import { ModalComponent } from '@app/components/global/modal/modal.component';
import { SchedulerStepService } from '../step/scheduler-step.service';
import { Subject, takeUntil } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ModalService implements OnDestroy {
  private overlayRef: OverlayRef | null = null;
  private isTimeoutModalVisible = false; 
  private serviceDestroy$ = new Subject<void>();

  constructor(
    private overlay: Overlay,
    private router: Router,
    private scheduler: SchedulerStepService
  ) {}

  public isTimeoutModalOpen(): boolean {
    return this.isTimeoutModalVisible;
  }

  private restartTimeoutTimerViaEvent(): void {
    try {
      const timeoutResetEvent = new CustomEvent('afcu-restart-timeout', {
        bubbles: true,
        detail: { triggeredBy: 'modal-close', timestamp: new Date().toISOString() }
      });
      
      setTimeout(() => {
        document.dispatchEvent(timeoutResetEvent);
      }, 50);
    } catch (error) {
      console.error("ModalService: Error dispatching custom event:", error);
    }
  }

  private forceRedirect(path: string): void {
    
    if (this.overlayRef) {
      this.overlayRef.detach();
      this.isTimeoutModalVisible = false;
      this.overlayRef = null;
    }
    
    try {
      // const targetPath = path.startsWith('/') ? path : `/${path}`;
      // const fullUrl = window.location.origin + targetPath;
      
      // window.location.href = fullUrl;

      this.router.navigate(["/splash"], {queryParams: {origin: "kiosk"}, queryParamsHandling: 'merge'})
    } catch (error) {
      window.location.reload();
    }
  }

  openModal(
    action: string,
    text: string,
    secondsLeft?: number,
    viewContainerRef?: ViewContainerRef,
  ): void {
    if (this.overlayRef) {
      return;
    }

    const isThisTimeoutModal = text === 'timeout';
    const isRedirect = text === 'navigation';

    const config: OverlayConfig = {
      positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
      width: '416px',
      height: '218px',
      hasBackdrop: true,
      backdropClass: 'overlay-backdrop'
    };

    this.overlayRef = this.overlay.create(config);

    if (isThisTimeoutModal) {
      this.isTimeoutModalVisible = true;
    } 

    const portal = new ComponentPortal(
      ModalComponent,
      viewContainerRef,
      this.createInjector(text, secondsLeft)
    );
    
    const componentRef = this.overlayRef.attach(portal);

    componentRef.instance.action.pipe(
      takeUntil(this.overlayRef.detachments())
    ).subscribe(ok => {
      
      if (ok) {
        // "Still Here" button or X button
        this.scheduler.reset();
        
        if (isThisTimeoutModal) {
          this.restartTimeoutTimerViaEvent();
          
          this.overlayRef?.detach();
        } else {
          if(isRedirect && action.includes('http')){
            window.open(action, '_blank')
          }else{
            if (action === '/locations')
              this.scheduler.branchSelected.set({} as Branch)
            
            this.overlayRef?.detach();
            this.router.navigate([action], { queryParamsHandling: 'merge' });
          } 
        }
      } else if (isThisTimeoutModal) {
        // GOODBYE BUTTON
        this.forceRedirect(action || '/splash');
      } else {
        // For non-timeout modals with 'No' button
        this.overlayRef?.detach();
      }
    });

    this.overlayRef.backdropClick().pipe(
      takeUntil(this.overlayRef.detachments())
    ).subscribe(() => {
      if (!isThisTimeoutModal) {
        this.overlayRef?.detach();
      } 
    });

    this.overlayRef.detachments().pipe(
        takeUntil(this.serviceDestroy$)
    ).subscribe(() => {
        if (isThisTimeoutModal) {
            this.isTimeoutModalVisible = false;
        }
        this.overlayRef = null;
    });
  }

  private createInjector(text: string, secondsLeft?: number): Injector {
    if (!this.overlayRef) {
        throw new Error("OverlayRef missing in createInjector");
    }
    return Injector.create({
      providers: [
        { provide: OverlayRef, useValue: this.overlayRef },
        { provide: 'action',   useValue: text },
        { provide: 'secondsLeft',  useValue: secondsLeft },
      ],
    });
  }

  ngOnDestroy(): void {
      this.serviceDestroy$.next();
      this.serviceDestroy$.complete();

      if (this.overlayRef) {
          this.overlayRef.dispose();
          this.overlayRef = null;
          this.isTimeoutModalVisible = false;
      }
  }
}